/**
 * Why the task list is empty, said out loud.
 *
 * The picker has four empty states and used to render the same nothing for all
 * of them. On 2026-09-22 that cost real time: two features had been created on
 * Work items and the picker offered nothing, because **hours hang off a task,
 * not a feature**, and no task existed under either. The screen said "Choose a
 * task" above an empty list and left the rest to be deduced.
 *
 * The owner/developer split (`docs/FINDINGS.md` F27) makes one of these states
 * routine rather than rare. "Only tasks assigned to me" is on by default, so
 * signed in as the owner, every task assigned to the developer profile is
 * invisible - and the screen looked identical to having no tasks at all.
 */

export interface TaskPickerEmptiness {
  loading: boolean;
  /** Tasks left after the filters and the assignment checkbox. */
  taskCount: number;
  onlyMine: boolean;
  /** Whether any of client, application or project is set. */
  narrowed: boolean;
}

const ONLY_MINE = 'Only tasks assigned to me';

/**
 * A sentence explaining an empty list, or null when there is nothing to
 * explain.
 *
 * Null while loading: the select's own placeholder already says so, and a
 * second message that appears and vanishes is noise.
 *
 * **Both levers are named when both are narrowing.** Naming one sends somebody
 * to clear filters that were never the problem, which is a worse answer than
 * saying nothing.
 */
export function taskPickerHint({
  loading,
  taskCount,
  onlyMine,
  narrowed,
}: TaskPickerEmptiness): string | null {
  if (loading || taskCount > 0) return null;

  if (onlyMine && narrowed) {
    return `No tasks match these filters and are assigned to you. Clear a filter, or untick ${ONLY_MINE}.`;
  }

  if (onlyMine) {
    return `No tasks are assigned to you. Untick ${ONLY_MINE} to see the rest.`;
  }

  if (narrowed) {
    return 'No tasks match these filters.';
  }

  // Nothing is narrowing anything, so there are genuinely no tasks. This is
  // the one that has to say where a task comes from: a feature is not enough,
  // and assuming otherwise is exactly the confusion that produced this file.
  return 'No tasks here yet. A feature is not enough - add a task to one, on the board or on Work items.';
}
